import {
  type EditActivity,
  type EditRoute,
  GeocoderAddressComponentTypes,
  type InputActivity,
  type NewActivity,
  NewActivityType,
  type NewDestination,
  type NewRoute,
  type RouteSettings,
  RouteType,
  type RoutingOptimization,
  type RoutingSettings,
} from '@cobliteam/api-lib/src/dashboard/routing/Definitions/Types';
import { addressComponentConcat, getAddressComponent } from '@cobliteam/api-lib/src/dashboard/routing/utils';
import { type Coordinate, type GoogleGeocoderAddressComponent, type GooglePlaceResult, getEndOfDayInMillis, getStartOfDayInMillis } from '@cobliteam/common-types';
import moment from 'moment';

const defaultRouteSettings: RouteSettings = {
  objectives: [],
  routingType: RouteType.MANUAL,
  workingHours: {
    startTime: getStartOfDayInMillis(),
    endTime: getEndOfDayInMillis(),
  },
  breakWindow: undefined,
};

const coordinateToGeometryMapper = (coordinate: Coordinate) => {
  const location = new google.maps.LatLng(coordinate.latitude, coordinate.longitude);
  const viewport = new google.maps.LatLngBounds(location);

  return {
    location,
    viewport,
  };
};

const newDestinationToAddressComponentMapper = (destination: NewDestination): GoogleGeocoderAddressComponent[] => {
  const { city, country, neighborhood, postalCode, state, streetAddress, streetComplement, streetNumber } = destination;

  return [
    city ? getAddressComponent(GeocoderAddressComponentTypes.CITY, city) : undefined,
    country ? getAddressComponent(GeocoderAddressComponentTypes.COUNTRY, country) : undefined,
    neighborhood ? getAddressComponent(GeocoderAddressComponentTypes.NEIGHBORHOOD, neighborhood) : undefined,
    postalCode ? getAddressComponent(GeocoderAddressComponentTypes.POSTAL_CODE, postalCode) : undefined,
    state ? getAddressComponent(GeocoderAddressComponentTypes.STATE, state) : undefined,
    streetAddress ? getAddressComponent(GeocoderAddressComponentTypes.STREET_ADDRESS, streetAddress) : undefined,
    streetComplement ? getAddressComponent(GeocoderAddressComponentTypes.STREET_COMPLEMENT, streetComplement) : undefined,
    streetNumber ? getAddressComponent(GeocoderAddressComponentTypes.STREET_NUMBER, streetNumber) : undefined,
  ].filter((value) => value !== undefined) as GoogleGeocoderAddressComponent[];
};

export const newDestinationToFormattedAddressMapper = (destination: NewDestination) => {
  const { city, state, streetAddress, neighborhood, postalCode, streetComplement } = destination;

  const neighborhoodAndCity = addressComponentConcat(', ', neighborhood, city);
  const stateAndPostalCode = addressComponentConcat(', ', state, postalCode);

  const components = [streetAddress, streetComplement, neighborhoodAndCity, stateAndPostalCode];

  return components.filter((component) => component).join(' - ');
};

export const newDestinationToGooglePlaceResultMapper = (destination: NewDestination): GooglePlaceResult => {
  const { streetAddress, coordinate } = destination;

  return {
    name: streetAddress || '',
    geometry: coordinateToGeometryMapper(coordinate),
    address_components: newDestinationToAddressComponentMapper(destination),
    formatted_address: newDestinationToFormattedAddressMapper(destination),
  };
};

export const activityToInputActivityMapper = (activity: NewActivity): InputActivity => {
  const { destination, plannedDuration, name, additionalInfo, completionStatus, visitStatus, timeWindows, phoneNumber, loadSize } = activity;

  return {
    destination: newDestinationToGooglePlaceResultMapper(destination),
    duration: plannedDuration,
    startTimeWindow: timeWindows?.[0]?.earliest ? moment(timeWindows?.[0]?.earliest).format('HH:mm') : undefined,
    endTimeWindow: timeWindows?.[0]?.latest ? moment(timeWindows?.[0]?.latest).format('HH:mm') : undefined,
    completionStatus,
    visitStatus,
    name,
    additionalInfo,
    phoneNumber,
    loadSize,
  };
};

export const activityToEditActivityMapper = (activity: NewActivity): EditActivity => ({
  id: activity.id,
  type: activity.type,
  position: activity.position,
  ...activityToInputActivityMapper(activity),
  loadSize: activity.loadSize,
});

export const newRouteToEditRouteMapper = (route: NewRoute): EditRoute => {
  const { activities, name, id, settings, deviceId, driverId, driverName, vehicleId, startTime, propagationType, recurrency } = route;

  const newRouteSettings = settings || defaultRouteSettings;

  return {
    id,
    name,
    deviceId,
    driverId,
    driverName,
    vehicleId,
    settings: newRouteSettings,
    activities: activities.map(activityToEditActivityMapper),
    startTime,
    propagationType,
    recurrency,
  };
};

export const newRouteToRoutingSettingsMapper = (route: NewRoute): RoutingSettings => {
  const { settings, activities } = route;

  const newActivities = activities.filter((activity) => activity.type !== NewActivityType.END).map(activityToInputActivityMapper);

  const endRouteDestination = activities.find((activity) => activity.type === NewActivityType.END)?.destination;

  const { objectives, routingType, workingHours, breakWindow } = settings || defaultRouteSettings;

  const optimization: RoutingOptimization = {
    objectives,
    workingHours,
    endRouteDestination,
    break: breakWindow,
    numVehicles: 1,
  };

  return {
    optimization,
    activities: newActivities,
    startTime: getStartOfDayInMillis(workingHours.startTime),
    type: routingType,
  };
};
